import React, { Component, createRef } from "react";

// Helper
import { checkCookie } from "../../Helper/Cookies";
import { apiFetch, getCurrentUser } from "../../Helper/Api";

// Components
import Header from "../../components/Header/Header";
import { hasModule } from "../../Helper/category";
import { Panel, Confirm, Modal } from "../../components/UI/Overlays";
import { EmptyState, TableWait, Chip, Tip } from "../../components/UI/Feedback";
import { Toolbar } from "../../components/UI/Controls";
import CourseBuilder from "./CourseBuilder";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";

// Properties
const properties = require("../../properties.json");

class AllCourses extends Component {
  constructor(props) {
    super(props);
    this.toast = createRef(null);
  }

  state = {
    globalFilter: null,
    data: [],

    // The builder, opened over the list: null means closed, "new" means a
    // course that does not exist yet, a number means that course.
    builderFor: null,

    // the rows are on their way
    tableLoading: true,

    // the course whose content (sections and lessons) is being built
    contentFor: null,

    // the course the teacher asked to delete, held while the confirm is up
    doomed: null,
    deleting: false,

    // ids resolved to the names the teacher knows
    materialsById: {},
    periodsById: {},
  };

  getCourses = () => {
    this.setState({ tableLoading: true });
    apiFetch("/courses")
      .then((data) => this.setState({ data, tableLoading: false }))
      .catch((error) => {
        this.setState({ tableLoading: false });
        console.error("Error:", error);
      });
  };

  // A row carries ids for its material and period; these two turn them into
  // names. Same shape as the lessons page.
  getMaterials = () => {
    // a gym account has no school structure (and the server refuses the call)
    if (!hasModule("SCHOOL_STRUCTURE")) return;
    const user = getCurrentUser();
    apiFetch("/lookup/materials", { query: { branchId: user?.branchId, teacherUserId: user?.userId } })
      .then((materials) => {
        const materialsById = {};
        materials.forEach((m) => {
          materialsById[m.id] = m.name;
        });
        this.setState({ materialsById });
      })
      .catch((error) => console.error("Error:", error));
  };

  getPeriods = () => {
    if (!hasModule("SCHOOL_STRUCTURE")) return;
    apiFetch("/periods")
      .then((periods) => {
        const periodsById = {};
        periods.forEach((p) => {
          periodsById[p.id] = p.name;
        });
        this.setState({ periodsById });
      })
      .catch((error) => console.error("Error:", error));
  };

  // The browser's confirm box is gone; the question is asked in the
  // product's own card. Everything after "yes" is the call it always was.
  deleteCourse = (id) => {
    var _this = this;

    this.setState({ deleting: true });

    apiFetch(`/courses/${id}`, { method: "DELETE" })
      .then(() => {
        _this.toast.current.show({ severity: "success", summary: "تمت العملية", detail: "تم الحذف بنجاح" });
        this.setState({ doomed: null, deleting: false });
        this.getCourses();
      })
      .catch((error) => {
        console.error("Error:", error);
        this.setState({ doomed: null, deleting: false });
        _this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: error.message || "خطأ في الإتصال" });
      });
  };

  componentDidMount() {
    document.title = properties.site_name + " | الدورات التعليمية";

    if (!checkCookie("name")) {
      window.localStorage.removeItem("currentTeacher");
      window.location.href = `/`;
    }

    this.getCourses();
    this.getMaterials();
    this.getPeriods();
  }

  render() {
    const { tableLoading, doomed, deleting, builderFor, contentFor } = this.state;

    // Building a course is something you do on this page now. The panel
    // mounts the very same CourseBuilder the /courses/add and
    // /courses/edit/:id routes mount, which still work on their own.
    const openNew = () => this.setState({ builderFor: "new" });
    const openEdit = (id) => this.setState({ builderFor: id });
    const closeBuilder = () => {
      this.setState({ builderFor: null });
      this.getCourses();
    };

    // building the content is its own job, reached from the row it belongs
    // to -- the sections and their items save themselves as they are made,
    // so closing is all there is to do at the end
    const openContent = (id) => this.setState({ contentFor: id });
    const closeContent = () => {
      this.setState({ contentFor: null });
      this.getCourses();
    };

    const actionBodyTemplate = (rowData) => (
      <React.Fragment>
        <Tip text="الأقسام والدروس">
          <Button
            icon="pi pi-list"
            className="p-button-rounded p-button-secondary p-ml-2"
            onClick={() => openContent(rowData.id)}
          />
        </Tip>
        <Tip text="تعديل">
          <Button
            icon="pi pi-pencil"
            className="p-button-rounded p-button-secondary p-ml-2"
            onClick={() => openEdit(rowData.id)}
          />
        </Tip>
        <Tip text="حذف">
          <Button
            icon="pi pi-trash"
            className="p-button-rounded p-button-danger"
            onClick={() => this.setState({ doomed: rowData })}
          />
        </Tip>
      </React.Fragment>
    );

    // the course as the teacher recognises it: its cover, its name, and the
    // line of description it was given
    const nameBody = (row) => {
      const cover = row.listImage || row.image || null;
      const about = String(row.description || "").trim();
      return (
        <span className="ui-row-lead">
          {cover ? (
            <img className="thumb" src={cover} alt="" loading="lazy" />
          ) : (
            <span className="thumb is-empty" aria-hidden="true">
              <i className="pi pi-book"></i>
            </span>
          )}
          <span className="tx">
            <b>{row.title}</b>
            <small>{about || "من غير وصف"}</small>
          </span>
        </span>
      );
    };

    // Arabic counts by one, two, a few and many -- "3" in a bubble says
    // nothing, "٣ أقسام" is read at a glance.
    const counted = (n, one, two, few, many) =>
      n === 1 ? one : n === 2 ? two : n <= 10 ? `${n} ${few}` : `${n} ${many}`;

    // What the course holds, read in one glance: how many lessons it has
    // altogether, and a dot per section -- filled where that section has
    // lessons in it, hollow where it is still waiting for them. The whole
    // breakdown, section by section, is on the cell itself.
    const contentBody = (row) => {
      const sections = row.sections || [];
      const items = sections.reduce((sum, s) => sum + (s.items || []).length, 0);

      if (!sections.length) {
        return <span className="ui-content is-none">لسه فاضية</span>;
      }

      const shown = sections.slice(0, 8);
      const rest = sections.length - shown.length;
      const breakdown = sections
        .map((s) => `${s.title}: ${(s.items || []).length}`)
        .join("  ·  ");

      return (
        <span className={items ? "ui-content" : "ui-content is-empty"} title={breakdown}>
          <b>
            {items
              ? counted(items, "درس واحد", "درسين", "دروس", "درس")
              : "من غير دروس"}
          </b>
          <span className="row">
            <span className="dots" aria-hidden="true">
              {shown.map((s) => (
                <i key={s.id} className={(s.items || []).length ? "on" : undefined}></i>
              ))}
              {rest > 0 ? <em>+{rest}</em> : null}
            </span>
            <small>
              في {counted(sections.length, "قسم واحد", "قسمين", "أقسام", "قسم")}
            </small>
          </span>
        </span>
      );
    };

    // where the course sits in the syllabus: its material, and the period
    // under it
    const placeBody = (row) => (
      <span className="ui-stack">
        <b>{this.state.materialsById[row.materialId] || "—"}</b>
        <small>{this.state.periodsById[row.periodId] || "—"}</small>
      </span>
    );

    const homeBody = (row) =>
      row.showOnHome ? (
        <Chip kind="ok">على الصفحة الرئيسية</Chip>
      ) : (
        <Chip kind="off">مخفية من الرئيسية</Chip>
      );

    return (
      <>
        <Header
          pageName="الدورات التعليمية"
          action={{ label: "دورة جديدة", icon: "pi pi-plus", onClick: openNew }}
        />

        <Toast ref={this.toast}></Toast>

        {/* the builder itself is untouched -- it is mounted here instead of
            being navigated to */}
        <Panel
          visible={!!builderFor}
          onHide={closeBuilder}
          title={builderFor === "new" ? "دورة جديدة" : "تعديل الدورة"}
          subtitle="احفظ بيانات الدورة، وبعدها ابنِ أقسامها ومحتواها"
        >
          {builderFor ? (
            <CourseBuilder
              inPanel
              view="details"
              courseId={builderFor === "new" ? null : builderFor}
              onSaved={closeBuilder}
            />
          ) : null}
        </Panel>

        {/* The content is a job of its own -- sections and the lessons in
            them -- so it opens from the row in its own modal, where each
            change saves itself as it is made. */}
        <Modal
          visible={!!contentFor}
          onHide={closeContent}
          title="أقسام الدورة ودروسها"
          width={900}
          footer={
            <button type="button" className="ui-btn" onClick={closeContent}>
              <i className="pi pi-check"></i>
              <span>خلصت، اقفل</span>
            </button>
          }
        >
          {contentFor ? (
            <CourseBuilder inPanel view="content" courseId={contentFor} />
          ) : null}
        </Modal>

        <Confirm
          visible={!!doomed}
          onHide={() => this.setState({ doomed: null })}
          name={doomed ? doomed.title : ""}
          note="الدورة هتتشال نهائياً بكل أقسامها ومحتواها، ومش هينفع ترجع عنها."
          busy={deleting}
          onConfirm={() => this.deleteCourse(doomed.id)}
        />

        <div className="content">
          <div className="container">
            <div className="card">
              <Card>
                <Toolbar
                  value={this.state.globalFilter || ""}
                  onSearch={(v) => this.setState({ globalFilter: v })}
                  placeholder="بحث في الدورات"
                  count={tableLoading ? null : `${this.state.data.length} دورة`}
                />

                <DataTable
                  sortable
                  globalFilter={this.state.globalFilter}
                  className="p-datatable-responsive-demo p-datatable-gridlines p-datatable-striped"
                  value={this.state.data}
                  paginator
                  rows={10}
                  rowsPerPageOptions={[10, 20, 50]}
                  currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                  emptyMessage={
                    tableLoading ? (
                      <TableWait />
                    ) : (
                      <EmptyState
                        title="لا يوجد دورات بعد"
                        hint="ابدأ بدورة واحدة، وضيف أقسامها ومحتواها بعد الحفظ"
                        icon="pi pi-book"
                        action={{ label: "دورة جديدة", onClick: openNew }}
                      />
                    )
                  }
                >
                  <Column sortable field="title" body={nameBody} header="الدورة"></Column>
                  <Column
                    sortable
                    field="materialId"
                    body={placeBody}
                    header="المادة والفترة"
                    style={{ width: "190px" }}
                  ></Column>
                  <Column
                    body={contentBody}
                    header="المحتوى"
                    style={{ width: "230px" }}
                  ></Column>
                  <Column
                    sortable
                    field="showOnHome"
                    body={homeBody}
                    header="الظهور"
                    style={{ width: "185px" }}
                  ></Column>
                  <Column header="تعديل / حذف" body={actionBodyTemplate}></Column>
                </DataTable>
              </Card>
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default AllCourses;
